import React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * Every-detail sheet for one vendo — opened by clicking a device name, in the
 * admin Fleet Device Status AND the operator device list (Ray 2026-10-18).
 * Compact: 460 px, one-line rows in two columns, a lone last row spans; header
 * and the Settings button stay put, only the body scrolls. Standard 8 px
 * corners; on a phone it docks to the bottom and slides up.
 */
export type DetailItem = [label: string, value: React.ReactNode, wide?: boolean];
export interface DetailSectionData {
   title: string;
   items?: DetailItem[];
   /** A ready-made block instead of rows (e.g. the System Info face). */
   content?: React.ReactNode;
}

export function DeviceDetailsSheet({
   title,
   deviceId,
   badge,
   sections,
   onClose,
   onSettings,
   settingsLabel = 'Device Settings',
   settingsBusy = false,
}: {
   title: string;
   deviceId: string;
   badge?: React.ReactNode;
   sections: DetailSectionData[];
   onClose: () => void;
   onSettings?: () => void;
   settingsLabel?: string;
   settingsBusy?: boolean;
}) {
   React.useEffect(() => {
      const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [onClose]);
   // Portalled to <body>: opened from a table cell, it would otherwise inherit
   // the cell's white-space: nowrap and bold font, and long lines (the System
   // Info notice) pushed the whole sheet sideways (Ray 2026-10-18).
   return createPortal(
      <DetailOverlay onClick={onClose}>
         <DetailDialog role="dialog" aria-label={`Details — ${title}`} onClick={(e) => e.stopPropagation()}>
            <DetailHead>
               <div>
                  <DetailTitle>{title}</DetailTitle>
                  <DetailSub>
                     <DetailId>{deviceId}</DetailId>
                     {badge}
                  </DetailSub>
               </div>
               <DetailClose type="button" aria-label="Close" onClick={onClose}>×</DetailClose>
            </DetailHead>
            <DetailBody>
               {sections
                  .filter((sec) => sec.content || sec.items?.length)
                  .map((sec) => (
                     <DetailSection key={sec.title}>
                        <h4>{sec.title}</h4>
                        {sec.content}
                        {sec.items && sec.items.length > 0 && (
                        <DetailTiles>
                           {sec.items.map(([label, value, wide], i, items) => (
                              // A row alone at the end spans the width: count only the
                              // half-width rows, so a wide one never leaves a gap.
                              <DetailTile
                                 key={label}
                                 $wide={!!wide || (i === items.length - 1 && items.filter((it) => !it[2]).length % 2 === 1)}
                              >
                                 <dt>{label}</dt>
                                 <dd>{value}</dd>
                              </DetailTile>
                           ))}
                        </DetailTiles>
                        )}
                     </DetailSection>
                  ))}
            </DetailBody>
            {onSettings && (
               <DetailFoot>
                  <DetailSettings type="button" onClick={onSettings} disabled={settingsBusy}>
                     {settingsLabel}
                  </DetailSettings>
               </DetailFoot>
            )}
         </DetailDialog>
      </DetailOverlay>,
      document.body
   );
}

const DetailId = styled.span`
   font-family: monospace;
   font-size: 11px;
   color: ${colors.GRAY5};
   white-space: nowrap;
`;
const detailSlideUp = keyframes`
   from { transform: translateY(24px); opacity: 0; }
   to { transform: none; opacity: 1; }
`;
const DetailOverlay = styled.div`
   position: fixed; inset: 0; z-index: 1000;
   background: rgba(0, 0, 0, 0.45);
   display: flex; align-items: center; justify-content: center; padding: 16px;
   @media (max-width: 640px) { align-items: flex-end; padding: 0; }
`;
/* Compact: 460 px wide, header + footer fixed, only the body scrolls. */
const DetailDialog = styled.div`
   background: ${colors.WHITE};
   border-radius: 8px; /* the standard window (Claim a transfer): 8 px, 18 px top on phones */
   width: 100%; max-width: 500px; max-height: 88vh;
   display: flex; flex-direction: column; overflow: hidden;
   white-space: normal; font-weight: 400; text-align: left; /* never inherit a table cell's */
   animation: ${detailSlideUp} 0.18s ease-out;
   @media (max-width: 640px) {
      max-width: none; max-height: 88vh;
      border-radius: 18px 18px 0 0;
      padding-bottom: env(safe-area-inset-bottom, 0px);
   }
`;
const DetailHead = styled.div`
   display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
   padding: 14px 18px 12px; border-bottom: 1px solid ${colors.GRAY13};
`;
const DetailTitle = styled.h3`margin: 0; font-size: 15px; color: ${colors.MAIN_DARK};`;
const DetailSub = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 3px;`;
const DetailClose = styled.button`border: 0; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: ${colors.GRAY5}; padding: 0 2px;`;
const DetailBody = styled.div`
   flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain;
   padding: 12px 18px 8px;
   display: flex; flex-direction: column; gap: 18px; /* clear break between sections */
`;
const DetailSection = styled.section`
   h4 { margin: 0 0 6px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: ${colors.GRAY5}; }
`;
/* Two columns of one-line rows (label left, value right); a lone last row
   takes the whole width. No grey boxes (Ray 2026-10-18). */
const DetailTiles = styled.dl`
   margin: 0;
   display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 16px;
`;
const DetailTile = styled.div<{ $wide: boolean }>`
   grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
   display: flex; align-items: center; justify-content: space-between; gap: 8px;
   padding: 7px 0;
   border-bottom: 1px solid ${colors.GRAY14};
   min-width: 0;
   line-height: 1.3;
   dt { font-size: 12px; color: ${colors.GRAY5}; white-space: nowrap; flex: none; }
   dd { margin: 0; font-size: 12.5px; font-weight: 600; color: ${colors.MAIN_DARK}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: right; min-width: 0; }
`;
const DetailFoot = styled.div`padding: 12px 18px 14px; border-top: 1px solid ${colors.GRAY13};`;
const DetailSettings = styled.button`
   width: 100%;
   border: 0; border-radius: 9px; padding: 9px 12px;
   background: ${colors.PRIMARY}; color: #fff; font-weight: 600; font-size: 14px; cursor: pointer;
   &:disabled { opacity: 0.6; cursor: default; }
`;
